/**
 * Time Utilities for GoTurkeyTransfer
 * 
 * Safe time formatting and validation to prevent:
 * - React render loops
 * - Invalid input values
 * - API 400 errors
 * - Console errors about invalid time format
 */

/**
 * Validates if a string is in valid HH:mm format
 * @param {string} val - Time string to validate
 * @returns {boolean} - True if valid HH:mm format
 */
export const isValidTime = (val) => {
  if (!val || typeof val !== 'string') return false;
  return /^([01]\d|2[0-3]):([0-5]\d)$/.test(val);
};

/**
 * Validates if a string is in valid YYYY-MM-DD format
 * @param {string} val - Date string to validate
 * @returns {boolean} - True if valid date format
 */
export const isValidDate = (val) => {
  if (!val || typeof val !== 'string') return false;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(val)) return false;
  
  // Also verify it's a real date
  const date = new Date(val);
  return !isNaN(date.getTime());
};

/**
 * Safely formats a Date object or timestamp to HH:mm
 * Returns empty string for invalid values
 * @param {Date|number|string} value - Value to format
 * @returns {string} - Formatted time string "HH:mm" or ""
 */
export const formatTimeSafe = (value) => {
  // Already valid format
  if (isValidTime(value)) return value;
  
  // Empty or falsy
  if (!value) return "";
  
  try {
    let date;
    
    if (value instanceof Date) {
      date = value;
    } else if (typeof value === 'number') {
      date = new Date(value);
    } else if (typeof value === 'string') {
      // Try to parse as ISO string or similar
      date = new Date(value);
    } else {
      return "";
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) return "";
    
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${hours}:${minutes}`;
  } catch {
    return "";
  }
};

/**
 * Safely formats a Date object or timestamp to YYYY-MM-DD
 * Returns empty string for invalid values
 * @param {Date|number|string} value - Value to format
 * @returns {string} - Formatted date string "YYYY-MM-DD" or ""
 */
export const formatDateSafe = (value) => {
  // Already valid format
  if (isValidDate(value)) return value;
  
  // Empty or falsy
  if (!value) return "";
  
  try {
    let date;
    
    if (value instanceof Date) {
      date = value;
    } else if (typeof value === 'number') {
      date = new Date(value);
    } else if (typeof value === 'string') {
      date = new Date(value);
    } else {
      return "";
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) return "";
    
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch {
    return "";
  }
};

/**
 * Sanitizes time input value before setting state
 * Returns the value if valid, empty string otherwise
 * @param {string} val - Input value
 * @returns {string} - Valid time or empty string
 */
export const sanitizeTimeInput = (val) => {
  if (!val) return "";
  
  // If already valid, return as-is
  if (isValidTime(val)) return val;
  
  // Try to extract HH:mm from string
  const match = val.match(/(\d{1,2}):(\d{2})/);
  if (match) {
    const hours = match[1].padStart(2, '0');
    const minutes = match[2];
    const formatted = `${hours}:${minutes}`;
    if (isValidTime(formatted)) return formatted;
  }
  
  return "";
};

/**
 * Sanitizes date input value before setting state
 * Returns the value if valid, empty string otherwise
 * @param {string} val - Input value
 * @returns {string} - Valid date or empty string
 */
export const sanitizeDateInput = (val) => {
  if (!val) return "";
  
  // If already valid, return as-is
  if (isValidDate(val)) return val;
  
  return "";
};

/**
 * Gets safe value for time input element
 * NEVER returns undefined, null, or invalid format
 * @param {string} val - State value
 * @returns {string} - Safe value for input
 */
export const getSafeTimeValue = (val) => {
  return isValidTime(val) ? val : "";
};

/**
 * Gets safe value for date input element
 * NEVER returns undefined, null, or invalid format
 * @param {string} val - State value
 * @returns {string} - Safe value for input
 */
export const getSafeDateValue = (val) => {
  return isValidDate(val) ? val : "";
};

export default {
  isValidTime,
  isValidDate,
  formatTimeSafe,
  formatDateSafe,
  sanitizeTimeInput,
  sanitizeDateInput,
  getSafeTimeValue,
  getSafeDateValue,
};
